// src/pages/user/UserMessages.tsx
// Route: /user/messages
//
// Two-panel inbox: left = thread list, right = open thread + compose.
// Collapses to single-column on mobile (list → thread transition).

import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../app/features/authSlice";
import {
  useGetMessagesQuery,
  useGetThreadQuery,
  useSendMessageMutation,
  useMarkMessageReadMutation,
  useGetChildrenQuery,
} from "../../app/api/parentApi";
import type {
  MessagePreview,
  ThreadMessage,
  SendMessageRequest,
} from "../../app/api/parentApi";

import { Check, MessagesSquare } from "lucide-react";
/* ─── atoms ─────────────────────────────────────────────────────── */

function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-xl bg-[#ebeff7] ${className}`} />
  );
}

function formatRelative(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateHeader(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/* ─── compose drawer ─────────────────────────────────────────────── */

function ComposeDrawer({
  threadId,
  subjectLock,
  onClose,
  onSent,
}: {
  threadId?: string;
  subjectLock?: string;
  onClose: () => void;
  onSent: (newThreadId: string) => void;
}) {
  const [send, { isLoading }] = useSendMessageMutation();
  const { data: childrenData } = useGetChildrenQuery();
  const children = childrenData?.data ?? [];

  const [subject, setSubject] = useState(subjectLock ?? "");
  const [body, setBody] = useState("");
  const [childId, setChildId] = useState("");
  const [error, setError] = useState("");

  const handleSend = async () => {
    if (!subject.trim()) {
      setError("Please add a subject");
      return;
    }
    if (!body.trim()) {
      setError("Please write a message");
      return;
    }
    setError("");
    try {
      const req: SendMessageRequest = {
        subject: subject.trim(),
        body: body.trim(),
        ...(childId ? { childId } : {}),
        ...(threadId ? { threadId } : {}),
      };
      const res = await send(req).unwrap();
      const tid = (res.data as any).threadId;
      setBody("");
      onSent(tid ?? threadId ?? "");
    } catch (e: any) {
      setError(e?.data?.error ?? "Failed to send message. Please try again.");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e] focus:ring-1 focus:ring-[#1b2d5e]/20 transition";

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#16213e]/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-[-8px_0_40px_rgba(27,45,94,0.14)] sm:max-w-[480px]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#ebeff7] px-6 py-5">
          <h2 className="text-base font-bold text-[#16213e]">
            {threadId ? "Reply to thread" : "New message"}
          </h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl text-[#8189a0] hover:bg-[#f1f3f8] transition"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Subject */}
          <div>
            <label className="mb-1.5 block text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
              Subject
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              readOnly={!!threadId}
              placeholder="What is this about?"
              className={
                inputCls +
                (threadId ? " bg-[#f9fafc] text-[#8189a0] cursor-default" : "")
              }
            />
          </div>

          {/* Child (optional) */}
          {!threadId && children.length > 0 && (
            <div>
              <label className="mb-1.5 block text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                About which child?{" "}
                <span className="font-normal normal-case text-[#a6adbf]">
                  (optional)
                </span>
              </label>
              <select
                value={childId}
                onChange={(e) => setChildId(e.target.value)}
                className={inputCls}
              >
                <option value="">— General enquiry —</option>
                {children.map((c: (typeof children)[number]) => (
                  <option key={c.id} value={c.id}>
                    {c.firstName} {c.lastName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Message body */}
          <div>
            <label className="mb-1.5 block text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
              Message
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              placeholder="Write your message here…"
              className={inputCls + " resize-none"}
            />
          </div>

          {error && (
            <p className="rounded-xl border border-[#f5c0c0] bg-[#fff3f3] px-3 py-2 text-xs font-semibold text-[#9b2a2a]">
              {error}
            </p>
          )}

          <div className="rounded-[14px] border border-[#dde2ec] bg-[#f9fafc] px-4 py-3 text-[12.5px] text-[#8189a0] leading-5">
            Messages are answered by the Future Smart Lab team, typically within
            1–2 business days.
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-[#ebeff7] px-6 py-4">
          <button
            onClick={handleSend}
            disabled={isLoading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1b2d5e] px-5 py-3 text-sm font-bold text-white shadow-[0_4px_16px_rgba(27,45,94,0.35)] transition hover:bg-[#24397a] disabled:opacity-60"
          >
            {isLoading ? "Sending…" : "Send message"}
          </button>
        </div>
      </div>
    </>
  );
}

/* ─── thread view ─────────────────────────────────────────────────── */

function ThreadView({
  threadId,
  onBack,
  onReply,
}: {
  threadId: string;
  onBack: () => void;
  onReply: () => void;
}) {
  const { data, isLoading } = useGetThreadQuery(threadId);
  const [markRead] = useMarkMessageReadMutation();
  const bottomRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const user = useSelector(selectUser);
  const messages: ThreadMessage[] = data?.data ?? [];

  // Scroll to bottom on load
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Mark unread messages read on open
  useEffect(() => {
    messages.forEach((m) => {
      if (!m.readByParent && m.direction === "outbound") {
        markRead(m.id).catch(() => {});
      }
    });
  }, [messages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group messages by date
  const grouped = messages.reduce(
    (acc: { date: string; msgs: ThreadMessage[] }[], msg) => {
      const key = new Date(msg.createdAt).toDateString();
      const last = acc[acc.length - 1];
      if (last && last.date === key) {
        last.msgs.push(msg);
      } else {
        acc.push({ date: key, msgs: [msg] });
      }
      return acc;
    },
    [],
  );

  const subject = messages[0]?.subject ?? "Thread";

  return (
    <div className="flex h-full flex-col">
      {/* Thread header */}
      <div className="flex flex-shrink-0 items-center gap-3 border-b border-[#ebeff7] px-4 py-4">
        <button
          onClick={onBack}
          className="flex h-8 w-8 items-center justify-center rounded-xl text-[#8189a0] hover:bg-[#f1f3f8] transition lg:hidden"
        >
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
            <path
              d="M10 4L6 8l4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-bold text-[#16213e] truncate">
            {subject}
          </h2>
          <p className="text-[12.5px] text-[#8189a0]">
            {messages.length} message{messages.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={onReply}
          className="flex-shrink-0 rounded-xl bg-[#1b2d5e] px-3 py-1.5 text-[12.5px] font-bold text-white transition hover:bg-[#24397a]"
        >
          Reply
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20 rounded-[18px]" />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-[#8189a0] py-8">
            No messages in this thread.
          </p>
        ) : (
          grouped.map((group: { date: string; msgs: ThreadMessage[] }) => (
            <div key={group.date}>
              {/* Date separator */}
              <div className="flex items-center gap-3 my-3">
                <div className="flex-1 h-px bg-[#ebeff7]" />
                <span className="text-[11.5px] font-bold text-[#a6adbf]">
                  {formatDateHeader(group.msgs[0].createdAt)}
                </span>
                <div className="flex-1 h-px bg-[#ebeff7]" />
              </div>

              {/* Message bubbles */}
              <div className="space-y-3">
                {group.msgs.map((msg: ThreadMessage) => {
                  const isParent = msg.direction === "inbound"; // parent sent it
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isParent ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={[
                          "max-w-[85%] rounded-[18px] px-4 py-3",
                          isParent
                            ? "bg-[#1b2d5e] text-white rounded-br-[6px]"
                            : "bg-white border border-[#e5e8ef] text-[#16213e] rounded-bl-[6px]",
                        ].join(" ")}
                      >
                        {/* Sender label */}
                        <p
                          className={`text-[11.5px] font-bold mb-1 ${isParent ? "text-white/70" : "text-[#1b2d5e]"}`}
                        >
                          {isParent ? "You" : msg.senderName}
                        </p>
                        {/* Body */}
                        <p
                          className={`text-sm leading-6 whitespace-pre-wrap ${isParent ? "text-white" : "text-[#16213e]"}`}
                        >
                          {msg.body}
                        </p>
                        {/* Time */}
                        <p
                          className={`text-[11.5px] mt-1.5 ${isParent ? "text-white/50 text-right" : "text-[#a6adbf]"}`}
                        >
                          {formatTime(msg.createdAt)}
                          {isParent && msg.readByParent && (
                            <span className="ml-1"><Check className="h-4 w-4" aria-hidden /></span>
                          )}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

/* ─── thread list item ───────────────────────────────────────────── */

function ThreadItem({
  preview,
  active,
  onClick,
}: {
  preview: MessagePreview;
  active: boolean;
  onClick: () => void;
}) {
  const hasUnread = (preview.unreadCount ?? 0) > 0;
  return (
    <button
      onClick={onClick}
      className={[
        "w-full flex items-start gap-3 rounded-[16px] px-3 py-3 text-left transition-all",
        active ? "bg-[#ebeff7]" : "hover:bg-[#f9fafc]",
      ].join(" ")}
    >
      {/* Avatar */}
      <span
        className={[
          "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] text-xs font-bold",
          hasUnread ? "bg-[#1b2d5e] text-white" : "bg-[#ebeff7] text-[#1b2d5e]",
        ].join(" ")}
      >
        FSL
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p
            className={`text-xs truncate ${hasUnread ? "font-bold text-[#16213e]" : "font-semibold text-[#3b4560]"}`}
          >
            {preview.subject}
          </p>
          <span className="flex-shrink-0 text-[11.5px] text-[#a6adbf]">
            {formatRelative(preview.createdAt)}
          </span>
        </div>
        <p className="text-[12.5px] text-[#8189a0] mt-0.5 truncate">
          {preview.from}
        </p>
        {hasUnread && (
          <span className="mt-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#efb0a4] px-1.5 text-[11px] font-bold text-[#5b2e28]">
            {preview.unreadCount}
          </span>
        )}
      </div>
    </button>
  );
}

/* ─── main page ──────────────────────────────────────────────────── */

export default function UserMessages() {
  const { data, isLoading, isError } = useGetMessagesQuery();
  const threads: MessagePreview[] = data?.data ?? [];

  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [showCompose, setShowCompose] = useState(false);
  const [replyThreadId, setReplyThreadId] = useState<string | undefined>();
  const [replySubject, setReplySubject] = useState<string | undefined>();

  // On desktop, auto-select first thread
  useEffect(() => {
    if (!activeThreadId && threads.length > 0) {
      setActiveThreadId(threads[0].threadId);
    }
  }, [threads.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReply = (threadId: string, subject: string) => {
    setReplyThreadId(threadId);
    setReplySubject(subject);
    setShowCompose(true);
  };

  const handleSent = (newThreadId: string) => {
    setShowCompose(false);
    setReplyThreadId(undefined);
    setReplySubject(undefined);
    setActiveThreadId(newThreadId || null);
  };

  const activePreview = threads.find((t) => t.threadId === activeThreadId);

  return (
    <div className="flex h-[calc(100vh-8rem)] overflow-hidden rounded-[22px] border border-[#e5e8ef] bg-white shadow-[0_2px_12px_rgba(27,45,94,0.06)]">
      {/* ── Left panel: thread list ── */}
      <div
        className={[
          "flex flex-col border-r border-[#ebeff7]",
          activeThreadId
            ? "hidden lg:flex lg:w-[300px] xl:w-[340px]"
            : "flex w-full lg:w-[300px] xl:w-[340px]",
          "flex-shrink-0",
        ].join(" ")}
      >
        {/* List header */}
        <div className="flex items-center justify-between border-b border-[#ebeff7] px-4 py-4">
          <div>
            <h1 className="text-sm font-bold text-[#16213e]">Messages</h1>
            {!isLoading && (
              <p className="text-[12.5px] text-[#8189a0]">
                {threads.length} thread{threads.length !== 1 ? "s" : ""}
              </p>
            )}
          </div>
          <button
            onClick={() => {
              setReplyThreadId(undefined);
              setReplySubject(undefined);
              setShowCompose(true);
            }}
            className="flex items-center gap-1.5 rounded-xl bg-[#1b2d5e] px-3 py-1.5 text-[12.5px] font-bold text-white transition hover:bg-[#24397a]"
          >
            <span className="text-sm leading-none">+</span> New
          </button>
        </div>

        {/* Thread list */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {isLoading ? (
            <div className="space-y-2 p-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 rounded-[16px]" />
              ))}
            </div>
          ) : isError ? (
            <p className="p-4 text-xs text-[#e57373]">
              Failed to load messages.
            </p>
          ) : threads.length === 0 ? (
            <div className="flex flex-col items-center py-12 px-4 text-center">
              <span className="mb-2"><MessagesSquare className="h-8 w-8" aria-hidden /></span>
              <p className="text-sm font-semibold text-[#16213e]">
                No messages yet
              </p>
              <p className="text-xs text-[#8189a0] mt-1">
                Send a message to the academy team.
              </p>
            </div>
          ) : (
            threads.map((t: MessagePreview) => (
              <ThreadItem
                key={t.threadId}
                preview={t}
                active={t.threadId === activeThreadId}
                onClick={() => setActiveThreadId(t.threadId)}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Right panel: open thread ── */}
      <div
        className={[
          "flex-1 min-w-0",
          activeThreadId ? "flex flex-col" : "hidden lg:flex lg:flex-col",
        ].join(" ")}
      >
        {activeThreadId ? (
          <ThreadView
            key={activeThreadId}
            threadId={activeThreadId}
            onBack={() => setActiveThreadId(null)}
            onReply={() =>
              handleReply(activeThreadId, activePreview?.subject ?? "")
            }
          />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center px-6">
            <span className="opacity-30"><MessagesSquare className="h-12 w-12" aria-hidden /></span>
            <p className="text-sm font-semibold text-[#8189a0]">
              Select a conversation
            </p>
            <p className="text-xs text-[#a6adbf]">
              Or start a new one with the academy team.
            </p>
            <button
              onClick={() => {
                setReplyThreadId(undefined);
                setShowCompose(true);
              }}
              className="mt-2 rounded-xl bg-[#1b2d5e] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#24397a]"
            >
              + New message
            </button>
          </div>
        )}
      </div>

      {/* Compose drawer */}
      {showCompose && (
        <ComposeDrawer
          threadId={replyThreadId}
          subjectLock={replySubject}
          onClose={() => {
            setShowCompose(false);
            setReplyThreadId(undefined);
            setReplySubject(undefined);
          }}
          onSent={handleSent}
        />
      )}
    </div>
  );
}
